import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import MISComp from "../mis_components/MISComp";
import ExpectedCollectionDue from "../mis_components/ExpectedCollectionDue";
import BounceClearStatus from "../mis_components/BounceClearStatus";
import { trackFeatureAction } from "../../../analytics/googleAnalytics";

// Styled Components
const Container = styled.div`
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  background: #f8f9fa;
  min-height: 100vh;
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0 30px;
  animation: ${fadeIn} 0.5s ease-out;
`;

const ButtonGroup = styled.div`
  display: flex;
  background: #fff;
  border-radius: 12px;
  padding: 6px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  position: relative;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 1250px;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    max-width: 300px;
  }
`;

const NavButton = styled.button`
  border: none;
  background: ${props =>
    props.active
      ? "linear-gradient(45deg, #ff512f, #dd2476)"
      : "#fff"};
  padding: 14px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  color: ${props => (props.active ? "#fff" : "#495057")};
  transition: all 0.3s ease;
  position: relative;
  flex: 1;
  min-width: 120px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props =>
      props.active
        ? "linear-gradient(45deg, #ff512f, #dd2476)"
        : "rgba(67, 97, 238, 0.08)"};
    color: ${props => (props.active ? "#fff" : "red")};
    transform: ${props =>
      props.active ? "none" : "translateY(-2px)"};
  }

  & span {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const ContentArea = styled.div`
  animation: ${fadeIn} 0.5s ease-out;
`;

// Main Component
const MIS = (props) => {
  const userDetails = props.userDetails;

  const [view, setView] = useState("disb_mis");

  const featureMap = {
    disb_mis: "Disbursement MIS",
    daily: "UTR Tracker",
    dailyStatus: "Daily Status",
    weekly: "ACH Status",
    monthly: "Expected Collection Due",
    bounce_clear: "Bounce & Clear",
  };

  useEffect(() => {
    const feature = featureMap[view];

    if (!feature) {
      return;
    }

    trackFeatureAction(
      "MIS",
      feature,
      "open"
    );
  }, [view]);

  const handleButtonClick = (selectedView) => {
    setView(selectedView);
  };


  /*
   * Group 13 should start on Expected Collection Due.
   */
  useEffect(() => {
    if (userDetails?.group_id === "13") {
      setView("monthly");
    }
  }, [userDetails?.group_id]);

  return (
    <Container>
      <ButtonContainer>
        <ButtonGroup>
          {userDetails?.group_id !== "13" && (
            <>
              <NavButton
                active={view === "disb_mis"}
                onClick={() =>
                  handleButtonClick("disb_mis")
                }
              >
                <span>DISBURSEMENT MIS</span>
              </NavButton>

              <NavButton
                active={view === "daily"}
                onClick={() =>
                  handleButtonClick("daily")
                }
              >
                <span>UTR TRACKER</span>
              </NavButton>

              <NavButton
                active={view === "dailyStatus"}
                onClick={() =>
                  handleButtonClick("dailyStatus")
                }
              >
                <span>DAILY STATUS</span>
              </NavButton>

              <NavButton
                active={view === "weekly"}
                onClick={() =>
                  handleButtonClick("weekly")
                }
              >
                <span>ACH STATUS</span>
              </NavButton>
            </>
          )}

          <NavButton
            active={view === "monthly"}
            onClick={() =>
              handleButtonClick("monthly")
            }
          >
            <span>EXPECTED COLLECTION DUE</span>
          </NavButton>

          {/* Bounce and Clear Tab */}
          <NavButton
            active={view === "bounce_clear"}
            onClick={() =>
              handleButtonClick("bounce_clear")
            }
          >
            <span>BOUNCE &amp; CLEAR</span>
          </NavButton>
        </ButtonGroup>
      </ButtonContainer>

      <ContentArea>
        {view === "weekly" && (
          <MISComp
            reportType="ACH"
            extraProp={props.userDetails}
          />
        )}

        {view === "monthly" && (
          <ExpectedCollectionDue
            extraProp={props.userDetails}
          />
        )}

        {view === "daily" && (
          <MISComp
            reportType="UTR"
            extraProp={props.userDetails}
          />
        )}

        {view === "disb_mis" && (
          <MISComp
            reportType="DISB MIS"
            extraProp={props.userDetails}
          />
        )}

        {view === "dailyStatus" && (
          <MISComp
            reportType="DAILY STATUS"
            extraProp={props.userDetails}
          />
        )}

        {view === "bounce_clear" && (
          <BounceClearStatus
            extraProp={props.userDetails}
          />
        )}
      </ContentArea>
    </Container>
  );
};

export default MIS;